/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useState } from "react";
import { InjectedConnector } from "@web3-react/injected-connector";
import { useWeb3React } from "@web3-react/core";
import config from "../config";
import { WalletType } from "../types";
import { useLocalStorageState } from "../hooks";
import { useContracts } from "./contracts";
import { Contract, utils } from "ethers";
import { signEthereumMessage } from "../ethereum";

export interface IWalletContext {
  connected: boolean;
  account: Maybe<string>;
  balance: number;
  tokenBalance: number;
  chainId: Maybe<number>;
  connect: (type: WalletType) => Promise<void>;
  disconnect: () => Promise<void>;
  updateBalance: () => Promise<void>;
  updateTokenBalance: () => Promise<void>;
  getTokenBalance: (
    userAddress: string,
    tokenAddress: string
  ) => Promise<number>;
  burn: (
    amount: number,
    setStakeWarning: React.Dispatch<React.SetStateAction<boolean>>,
    setPending: React.Dispatch<React.SetStateAction<boolean>>,
    setDisabledStaking: React.Dispatch<React.SetStateAction<boolean>>
  ) => Promise<void>;
  signMessage: (
    message: string,
    setMessage: React.Dispatch<React.SetStateAction<string | void>>,
    setStakeWarning: React.Dispatch<React.SetStateAction<boolean>>,
    setPending: React.Dispatch<React.SetStateAction<boolean>>,
    setDisabledStaking: React.Dispatch<React.SetStateAction<boolean>>
  ) => Promise<void>;
}

export const metamaskInjected = new InjectedConnector({
  supportedChainIds: [1, 3, 4, 5, 42, 56, 97],
});

export const WalletContext = React.createContext<Maybe<IWalletContext>>(null);

export const WalletProvider = ({ children = null as any }) => {
  const { provider } = useContracts();
  const { activate, deactivate, active, chainId, account } = useWeb3React();
  const [walletType, setWalletType] = useLocalStorageState("wallet_type", "");
  const [connected, setConnected] = useState<boolean>(false);
  const [balance, setBalance] = useState<number>(0);
  const [tokenBalance, setTokenBalance] = useState<number>(0);

  const connect = useCallback(
    async (type: WalletType = WalletType.MetaMask) => {
      try {
        if (type === WalletType.MetaMask) {
          await activate(metamaskInjected);
        }
        setWalletType(type.toString());
      } catch (err) {
        console.error("Wallet connect failed!");
      }
    },
    [activate, setWalletType]
  );

  const disconnect = async () => {
    await deactivate();
    setWalletType(null);
  };

  useEffect(() => {
    if (walletType) {
      if (walletType === "metamask") {
        connect(WalletType.MetaMask);
      } else if (walletType === "walletconnect") {
        connect(WalletType.WalletConnect);
      }
    }
  }, [connect, activate, walletType]);

  useEffect(() => {
    if (active) {
      if (chainId) {
        if (chainId === config.networkId) {
          setConnected(true);
        } else {
          deactivate();
          console.error(`Please connect ${config.networkName}!`);
        }
      }
    } else {
      setConnected(false);
    }
  }, [active, chainId, deactivate]);
  const updateBalance = useCallback(async () => {
    if (account) {
      const res = await provider.getBalance(account);
      setBalance(Number(utils.formatEther(res)));
    } else {
      setBalance(0);
    }
  }, [account, provider]);

  const updateTokenBalance = useCallback(async () => {
    if (account) {
      const tokenContract = new Contract(config.tokenAddress, config.tokenAbi, provider);

      try {
        const res = await tokenContract.balanceOf(account, { from: account, gasLimit: 30000 });
        setTokenBalance(Math.floor(Number(utils.formatEther(res))));
      } catch (err: any) {
        console.error(err);
      }
    } else {
      setTokenBalance(0);
    }
  }, [account, provider]);

  useEffect(() => {
    updateBalance();
    updateTokenBalance();
  }, [updateBalance, updateTokenBalance]);

  const getTokenBalance = async (userAddress: string, tokenAddress: string) => {
    const tokenContract = new Contract(tokenAddress, config.tokenAbi, provider);

    try {
      const res = await tokenContract.balanceOf(userAddress);
      return Math.floor(Number(utils.formatEther(res)));
    } catch (err: any) {
      console.error(err);
    }
    return 0;
  };

  const signMessage = async (
    message: string,
    setMessage: React.Dispatch<React.SetStateAction<string | void>>,
    setStakeWarning: React.Dispatch<React.SetStateAction<boolean>>,
    setPending: React.Dispatch<React.SetStateAction<boolean>>,
    setDisabledStaking: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!account) return;
    setPending(true);
    setDisabledStaking(true);
    try {
      const signature = await signEthereumMessage(provider, account, message);
      setMessage(signature);
      setStakeWarning(true);
    } catch (error) {
      setMessage("error");
    } finally {
      setPending(false);
      setDisabledStaking(false);
    }
  };

  const burn = async (
    amount: number,
    setStakeWarning: React.Dispatch<React.SetStateAction<boolean>>,
    setPending: React.Dispatch<React.SetStateAction<boolean>>,
    setDisabledStaking: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!account) return;
    setPending(true);
    setDisabledStaking(true);
    try {
      const tokenContract = new Contract(
        config.tokenAddress, config.tokenAbi, provider.getSigner(account)
      );
      const transaction = await tokenContract.burn(
        utils.parseEther(amount.toString()), utils.id("epoch1")
      );
      await transaction.wait(1);
      setStakeWarning(true);
    } catch (error) {
      console.error(error);
    } finally {
      setPending(false);
      setDisabledStaking(false);
    }
  };

  return (
    <WalletContext.Provider
      value={{
        connected,
        account,
        balance,
        tokenBalance,
        chainId,
        connect,
        disconnect,
        updateBalance,
        updateTokenBalance,
        getTokenBalance,
        burn,
        signMessage,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);

  if (!context) {
    throw new Error("Component rendered outside the provider tree");
  }

  return context;
};
